import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../components/ui/PageHeader';
import ViewToggle, { useViewMode } from '../components/ui/ViewToggle';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SendRounded from '@mui/icons-material/SendRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import PersonAddRounded from '@mui/icons-material/PersonAddRounded';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import VideoFileRounded from '@mui/icons-material/VideoFile';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import Swal from 'sweetalert2';
import { io, Socket } from 'socket.io-client';
import {
  getChatMessages,
  getChatRequests,
  getProfile,
  listChatThreads,
  sendChatMessage,
} from '../services/api';
import { getStoredUser, getToken } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

/* -------------------- Types -------------------- */
type SlimUser = {
  _id: string;
  fullName?: string;
  email?: string;
  username?: string;
  avatar?: string;
};

type ChatThread = {
  _id: string;
  participants: SlimUser[];
  requestedBy: SlimUser;
  requestedTo: SlimUser;
  status: 'pending' | 'active' | 'declined';
  lastMessageAt?: string;
  lastMessageSnippet?: string;
};

type MsgAttachment = {
  url: string;
  originalName?: string;
  mimeType?: string;
  size?: number;
};

type ChatMessageDto = {
  _id: string;
  sender: SlimUser;
  receiver: SlimUser;
  text?: string;
  attachments?: MsgAttachment[];
  createdAt: string;
};

type RequestLists = {
  incoming: ChatThread[];
  outgoing: ChatThread[];
};

/* -------------------- Helpers -------------------- */
const safeUser = (): SlimUser | null => getStoredUser<SlimUser>();
const displayName = (u?: SlimUser | null) => u?.fullName || u?.username || u?.email || 'Unknown user';
const shortText = (txt?: string | null) => {
  if (!txt) return '';
  return txt.length > 60 ? `${txt.slice(0, 60)}…` : txt;
};
const formatTime = (value?: string) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

const isImage = (mime?: string) => !!mime?.startsWith('image/');
const isVideo = (mime?: string) => !!mime?.startsWith('video/');
const isPdf   = (mime?: string) => mime === 'application/pdf';

const HARD_MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB absolute ceiling

/* -------------------- Attachment bubble -------------------- */
const AttachmentItem = ({
  att,
  mine,
  onImageClick,
  onPdfClick,
}: {
  att: MsgAttachment;
  mine: boolean;
  onImageClick: (url: string) => void;
  onPdfClick: (url: string) => void;
}) => {
  if (isImage(att.mimeType)) {
    return (
      <Box
        sx={{
          mt: 0.5,
          borderRadius: 1,
          overflow: 'hidden',
          maxWidth: 280,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: mine ? 'rgba(255,255,255,0.2)' : 'divider',
        }}
        onClick={() => onImageClick(att.url)}
      >
        <img
          src={att.url}
          alt={att.originalName || 'image'}
          style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 220 }}
        />
      </Box>
    );
  }

  if (isVideo(att.mimeType)) {
    return (
      <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden', maxWidth: 320 }}>
        <video
          controls
          style={{ width: '100%', display: 'block', borderRadius: 12 }}
        >
          <source src={att.url} type={att.mimeType} />
          Your browser does not support the video tag.
        </video>
      </Box>
    );
  }

  if (isPdf(att.mimeType)) {
    return (
      <Box
        sx={{
          mt: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderRadius: 1,
          bgcolor: mine ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: mine ? 'rgba(255,255,255,0.2)' : 'divider',
          maxWidth: 260,
        }}
      >
        <PictureAsPdfRounded sx={{ color: mine ? '#ffcdd2' : 'error.main', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ color: mine ? '#fff' : 'text.primary', fontWeight: 600 }}>
            {att.originalName || 'Document.pdf'}
          </Typography>
          <Typography variant="caption" sx={{ color: mine ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
            PDF
          </Typography>
        </Box>
        <Tooltip title="Preview">
          <IconButton
            size="small"
            onClick={() => onPdfClick(att.url)}
            sx={{ color: mine ? '#fff' : 'primary.main', flexShrink: 0 }}
          >
            <InsertDriveFileRounded fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton
            size="small"
            component="a"
            href={att.url}
            download={att.originalName || 'document.pdf'}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: mine ? '#fff' : 'primary.main', flexShrink: 0 }}
          >
            <DownloadRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // Generic file
  return (
    <Box
      component="a"
      href={att.url}
      download={att.originalName || 'file'}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        mt: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        borderRadius: 1,
        bgcolor: mine ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)',
        border: '1px solid',
        borderColor: mine ? 'rgba(255,255,255,0.2)' : 'divider',
        maxWidth: 260,
        textDecoration: 'none',
        '&:hover': { opacity: 0.85 },
      }}
    >
      <InsertDriveFileRounded sx={{ color: mine ? '#fff' : 'primary.main', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ color: mine ? '#fff' : 'text.primary', fontWeight: 600 }}>
          {att.originalName || 'File'}
        </Typography>
        {att.size != null && (
          <Typography variant="caption" sx={{ color: mine ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
            {(att.size / 1024).toFixed(0)} KB
          </Typography>
        )}
      </Box>
      <DownloadRounded fontSize="small" sx={{ color: mine ? '#fff' : 'primary.main', flexShrink: 0 }} />
    </Box>
  );
};

/* -------------------- Selected-file preview chip -------------------- */
const AttachPreviewChip = ({
  file,
  previewUrl,
  onRemove,
}: {
  file: File;
  previewUrl: string | null;
  onRemove: () => void;
}) => (
  <Box
    sx={{
      position: 'relative',
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.5,
      flexShrink: 0,
    }}
  >
    {previewUrl ? (
      <Box
        sx={{
          width: 64, height: 64, borderRadius: 1,
          overflow: 'hidden', border: '1px solid', borderColor: 'divider',
        }}
      >
        <img src={previewUrl} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </Box>
    ) : (
      <Box
        sx={{
          width: 64, height: 64, borderRadius: 1,
          border: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.25,
        }}
      >
        {file.type.startsWith('video/') ? (
          <VideoFileRounded fontSize="small" color="action" />
        ) : file.type === 'application/pdf' ? (
          <PictureAsPdfRounded fontSize="small" color="error" />
        ) : (
          <InsertDriveFileRounded fontSize="small" color="action" />
        )}
        <Typography variant="caption" sx={{ fontSize: 9, px: 0.5, textAlign: 'center' }} noWrap>
          {file.name.slice(0, 8)}
        </Typography>
      </Box>
    )}
    <IconButton
      size="small"
      onClick={onRemove}
      sx={{
        position: 'absolute',
        top: -6, right: -6,
        width: 18, height: 18,
        bgcolor: 'error.main', color: '#fff',
        '&:hover': { bgcolor: 'error.dark' },
      }}
    >
      <CloseRounded sx={{ fontSize: 12 }} />
    </IconButton>
  </Box>
);

/* ==================== Main Component ==================== */
const Chat = () => {
  const me = safeUser();
  const myId = me?._id ?? '';
  const navigate = useNavigate();

  const [messageText, setMessageText] = useState('');
  const [attachFiles, setAttachFiles] = useState<File[]>([]);
  const [requests, setRequests] = useState<RequestLists>({ incoming: [], outgoing: [] });
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [threadView, setThreadView] = useViewMode('quillstack.chat.view', 'list');
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [threadsFetched, setThreadsFetched] = useState(false);
  const [loadedMessageThreads, setLoadedMessageThreads] = useState<Set<string>>(new Set());

  // Per-user chat attachment size limit (MB); null = unlimited (capped at 50 MB)
  const [chatFileSizeLimitMB, setChatFileSizeLimitMB] = useState<number | null>(null);

  // Lightbox / PDF preview state
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [pdfViewUrl, setPdfViewUrl] = useState<string | null>(null);

  const endRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const selectedRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Local preview URLs for selected image files
  const attachPreviews = useMemo(
    () => attachFiles.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null)),
    [attachFiles]
  );
  useEffect(() => {
    return () => attachPreviews.forEach((url) => url && URL.revokeObjectURL(url));
  }, [attachPreviews]);

  const selectedThread = useMemo(
    () => threads.find((t) => t._id === selectedThreadId) || null,
    [threads, selectedThreadId]
  );

  const otherForThread = useCallback(
    (thread: ChatThread | null): SlimUser | null => {
      if (!thread) return null;
      const other = thread.participants?.find((p) => p._id !== myId);
      return other || thread.requestedBy || thread.requestedTo || null;
    },
    [myId]
  );

  const otherUser = useMemo(() => otherForThread(selectedThread), [otherForThread, selectedThread]);

  const scrollToBottom = () => endRef.current?.scrollIntoView({ behavior: 'smooth' });

  const pushMessageIfNew = useCallback((msg: ChatMessageDto) => {
    setMessages((prev) => {
      if (prev.some((m) => m._id === msg._id)) return prev;
      return [...prev, msg];
    });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages]);

  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const data = await getChatRequests();
      setRequests({ incoming: data.incoming || [], outgoing: data.outgoing || [] });
    } catch (err: any) {
      console.error(err);
      Swal.fire('Error', err?.response?.data?.message || 'Could not load requests', 'error');
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  const loadThreads = useCallback(async () => {
    setLoadingThreads(true);
    try {
      const data = await listChatThreads();
      const list: ChatThread[] = data.threads || [];
      setThreads(list);
      setSelectedThreadId((prev) => {
        if (prev) return prev;
        return list.length ? list[0]._id : null;
      });
    } catch (err: any) {
      console.error(err);
      Swal.fire('Error', err?.response?.data?.message || 'Could not load chats', 'error');
    } finally {
      setLoadingThreads(false);
      setThreadsFetched(true);
    }
  }, []);

  useEffect(() => {
    loadRequests();
    loadThreads();
    getProfile().then((p) => {
      if (p?.chatFileSizeLimit != null) setChatFileSizeLimitMB(p.chatFileSizeLimit);
    }).catch(() => {});
  }, [loadRequests, loadThreads]);

  const loadMessages = useCallback(async (threadId: string, showErrors = true) => {
    setLoadingMessages(true);
    try {
      const data = await getChatMessages(threadId);
      setMessages(data.messages || []);
      setLoadedMessageThreads((prev) => { const n = new Set(prev); n.add(threadId); return n; });
    } catch (err: any) {
      console.error(err);
      if (showErrors) Swal.fire('Error', err?.response?.data?.message || 'Could not load messages', 'error');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedThreadId) { setMessages([]); return; }
    loadMessages(selectedThreadId);
  }, [selectedThreadId, loadMessages]);

  useEffect(() => { selectedRef.current = selectedThreadId; }, [selectedThreadId]);

  // Socket
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const base = (process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api').replace(/\/api$/, '');
    const socket = io(base, { transports: ['websocket', 'polling'], auth: { token }, reconnectionAttempts: 5, withCredentials: true });
    socketRef.current = socket;

    socket.on('connect_error', (err: any) => {
      console.error('Socket error:', err?.message || err);
      Swal.fire('Socket error', err?.message || 'Unable to connect to chat socket.', 'error');
    });

    socket.on('chat:message', (payload: { threadId: string; message: ChatMessageDto }) => {
      const { threadId, message } = payload || {};
      if (!threadId || !message) return;
      setThreads((prev) =>
        prev.map((t) =>
          t._id === threadId
            ? { ...t, lastMessageAt: message.createdAt, lastMessageSnippet: message.text || '[attachment]' }
            : t
        )
      );
      if (threadId === selectedRef.current) pushMessageIfNew(message);
    });

    const refetch = () => { loadRequests(); loadThreads(); };
    socket.on('chat:request', refetch);
    socket.on('chat:request:update', refetch);

    return () => { socket.disconnect(); socketRef.current = null; };
  }, [loadRequests, loadThreads, pushMessageIfNew]);

  /* ---- File selection ---- */
  const handleAttachSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    const maxBytes = chatFileSizeLimitMB != null
      ? Math.min(chatFileSizeLimitMB * 1024 * 1024, HARD_MAX_FILE_BYTES)
      : HARD_MAX_FILE_BYTES;
    const limitLabel = chatFileSizeLimitMB != null ? `${chatFileSizeLimitMB} MB` : '50 MB';
    const valid = picked.filter((f) => f.size <= maxBytes);
    const rejected = picked.filter((f) => f.size > maxBytes);
    if (rejected.length) Swal.fire('Files too large', `Each file must be ${limitLabel} or smaller.`, 'warning');
    const remaining = 10 - attachFiles.length;
    if (valid.length > remaining) {
      Swal.fire('Too many files', `You can attach up to 10 files per message. ${attachFiles.length} already selected.`, 'warning');
    }
    setAttachFiles((prev) => [...prev, ...valid.slice(0, remaining)]);
    e.target.value = '';
  };

  const removeAttach = (idx: number) => setAttachFiles((prev) => prev.filter((_, i) => i !== idx));

  /* ---- Send ---- */
  const handleSendMessage = async () => {
    if (!selectedThreadId) return;
    if (!messageText.trim() && !attachFiles.length) return;
    setSendingMessage(true);
    try {
      const res = await sendChatMessage(selectedThreadId, messageText.trim(), attachFiles.length ? attachFiles : undefined);
      const msg: ChatMessageDto | undefined = res?.message;
      setMessageText('');
      setAttachFiles([]);
      if (!msg) await loadMessages(selectedThreadId, false);
      await loadThreads();
    } catch (err: any) {
      console.error(err);
      Swal.fire('Error', err?.response?.data?.message || 'Could not send message', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const pendingCount = requests.incoming.length;
  const canSend = !sendingMessage && (!!messageText.trim() || attachFiles.length > 0);

  return (
    <Stack spacing={3}>
      {/* Header */}
      <PageHeader
        title="Chat"
        subtitle="Pick a conversation from the left pane to start chatting."
        actions={
        <Tooltip title="Friend Requests">
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton
              onClick={() => navigate('/chat/friends')}
              sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <PersonAddRounded />
            </IconButton>
            {pendingCount > 0 && (
              <Box sx={{ position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%', bgcolor: 'error.main', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, pointerEvents: 'none' }}>
                {pendingCount > 9 ? '9+' : pendingCount}
              </Box>
            )}
          </Box>
        </Tooltip>
        }
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* ===== Thread list ===== */}
        <Paper
          sx={{
            p: 2, width: { xs: '100%', md: 360 }, flexShrink: 0, borderRadius: '26px', overflow: 'hidden',
            position: 'relative',
            border: '1px solid rgba(0,180,219,.22)',
            boxShadow: '0 16px 34px rgba(0,131,176,.16)',
            '&::before': {
              content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 5,
              background: 'linear-gradient(90deg,#00B4DB,#0083B0)',
            },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} spacing={1}>
            <Typography fontWeight={800}>Active chats</Typography>
            <Box sx={{ flex: 1 }} />
            <ViewToggle value={threadView} onChange={setThreadView} />
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={loadThreads} disabled={loadingThreads}>
                  {loadingThreads ? <CircularProgress size={18} /> : <RefreshRounded fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {loadingThreads && !threadsFetched ? (
            <List dense>
              {[0, 1, 2, 3].map((i) => (
                <ListItemButton key={i} sx={{ borderRadius: 1 }}>
                  <Skeleton variant="circular" width={36} height={36} sx={{ mr: 1.5 }} />
                  <ListItemText primary={<Skeleton width="70%" />} secondary={<Skeleton width="55%" />} />
                  <Skeleton width={48} height={12} />
                </ListItemButton>
              ))}
            </List>
          ) : threads.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Accept a request to start chatting.</Typography>
          ) : (
            <Box
              sx={threadView === 'grid'
                ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }
                : { display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {threads.map((thread) => {
                const other = otherForThread(thread);
                const active = selectedThreadId === thread._id;
                const initial = (displayName(other).charAt(0) || 'U').toUpperCase();
                const grid = threadView === 'grid';
                return (
                  <Box
                    key={thread._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedThreadId(thread._id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedThreadId(thread._id); }}
                    sx={{
                      position: 'relative', overflow: 'hidden', cursor: 'pointer',
                      display: 'flex',
                      flexDirection: grid ? 'column' : 'row',
                      alignItems: 'center',
                      textAlign: grid ? 'center' : 'left',
                      gap: grid ? 0.75 : 1.5,
                      p: grid ? 1.5 : 1.25,
                      borderRadius: '20px',
                      color: active ? '#fff' : 'inherit',
                      background: active
                        ? 'linear-gradient(135deg, #0088B5, #005A80)'
                        : (t) => (t.palette.mode === 'light' ? 'linear-gradient(135deg, rgba(0,180,219,.08), rgba(0,131,176,.03))' : 'rgba(0,180,219,.10)'),
                      border: '1px solid',
                      borderColor: active ? 'transparent' : 'rgba(0,180,219,.18)',
                      boxShadow: active ? '0 12px 24px rgba(0,131,176,.40), inset 0 1px 0 rgba(255,255,255,.4)' : 'none',
                      transition: 'transform .2s ease, box-shadow .2s ease',
                      '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 14px 26px rgba(0,131,176,.28)' },
                      '&::before': active ? {
                        content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                        background: 'linear-gradient(135deg, rgba(255,255,255,.35), rgba(255,255,255,0) 45%)',
                      } : undefined,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: grid ? 54 : 42, height: grid ? 54 : 42, fontWeight: 800,
                        color: '#fff',
                        background: active ? 'rgba(255,255,255,.28)' : 'linear-gradient(135deg, #0088B5, #005A80)',
                        border: '2px solid rgba(255,255,255,.7)',
                        boxShadow: '0 6px 14px rgba(0,131,176,.35)',
                      }}
                    >
                      {initial}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: grid ? undefined : 1, width: grid ? '100%' : undefined }}>
                      <Typography fontWeight={800} noWrap>{displayName(other)}</Typography>
                      <Typography variant="body2" noWrap sx={{ opacity: active ? 0.9 : 0.7 }}>
                        {shortText(thread.lastMessageSnippet) || 'No messages yet'}
                      </Typography>
                    </Box>
                    {thread.lastMessageAt && (
                      <Typography variant="caption" sx={{ opacity: active ? 0.9 : 0.65, whiteSpace: 'nowrap' }}>
                        {new Date(thread.lastMessageAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        {/* ===== Chat window ===== */}
        <Paper
          sx={{
            p: 0, flex: 1, minHeight: 420, display: 'flex', flexDirection: 'column', overflow: 'hidden',
            borderRadius: '26px', border: '1px solid rgba(0,180,219,.22)', boxShadow: '0 16px 34px rgba(0,131,176,.16)',
          }}
        >
          {loadingThreads && !threadsFetched ? (
            /* skeleton */
            <>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}><Skeleton width="60%" /><Skeleton width="40%" /></Box>
                <Skeleton variant="rounded" width={72} height={20} />
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, background: (t) => t.palette.mode === 'light' ? 'linear-gradient(180deg, #DDF1F8 0%, #EDE8FA 100%)' : 'background.default', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Stack key={i} direction="row" justifyContent={i % 2 === 0 ? 'flex-start' : 'flex-end'}>
                    <Skeleton variant="rounded" width="68%" height={64} sx={{ borderRadius: 1 }} />
                  </Stack>
                ))}
              </Box>
              <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', gap: 1, alignItems: 'center' }}>
                <Skeleton variant="rounded" width="100%" height={46} />
                <Skeleton variant="rounded" width={92} height={46} />
              </Box>
            </>
          ) : selectedThread ? (
            <>
              {/* Thread header */}
              <Box
                sx={{
                  px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, color: '#fff', position: 'relative',
                  background: 'linear-gradient(120deg, #0088B5, #005A80)',
                  '&::before': {
                    content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                    background: 'linear-gradient(180deg, rgba(255,255,255,.28), rgba(255,255,255,0) 60%)',
                  },
                }}
              >
                <Avatar sx={{ width: 44, height: 44, fontWeight: 800, bgcolor: 'rgba(255,255,255,.28)', border: '2px solid rgba(255,255,255,.7)' }}>
                  {(displayName(otherUser).charAt(0) || 'U').toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                  <Typography fontWeight={800} noWrap>{displayName(otherUser)}</Typography>
                  <Typography variant="body2" noWrap sx={{ opacity: 0.9 }}>{otherUser?.email || 'Direct chat'}</Typography>
                </Box>
                <Chip size="small" label="Active" sx={{ position: 'relative', bgcolor: 'rgba(255,255,255,.92)', color: '#0083B0', fontWeight: 800 }} />
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, background: (t) => t.palette.mode === 'light' ? 'linear-gradient(180deg, #DDF1F8 0%, #EDE8FA 100%)' : 'background.default', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {loadingMessages && !loadedMessageThreads.has(selectedThread._id) ? (
                  <Stack sx={{ flex: 1, gap: 1.25 }}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Stack key={i} direction="row" justifyContent={i % 2 === 0 ? 'flex-start' : 'flex-end'}>
                        <Skeleton variant="rounded" width="68%" height={64} sx={{ borderRadius: 1 }} />
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <>
                    {messages.map((msg) => {
                      const mine = msg.sender?._id === myId;
                      const atts = msg.attachments || [];
                      const hasText = !!msg.text?.trim();
                      return (
                        <Stack key={msg._id} direction="row" justifyContent={mine ? 'flex-end' : 'flex-start'}>
                          <Box sx={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                            {/* Media (images + videos) rendered outside bubble */}
                            {atts.filter((a) => isImage(a.mimeType) || isVideo(a.mimeType)).map((att, i) => (
                              <AttachmentItem
                                key={i}
                                att={att}
                                mine={mine}
                                onImageClick={setLightboxUrl}
                                onPdfClick={setPdfViewUrl}
                              />
                            ))}

                            {/* Text bubble (+ PDF / generic file attachments inside) */}
                            {(hasText || atts.some((a) => !isImage(a.mimeType) && !isVideo(a.mimeType))) && (
                              <Box sx={{ background: mine ? 'linear-gradient(135deg, #0088B5, #005A80)' : undefined, bgcolor: mine ? undefined : 'background.paper', color: mine ? '#fff' : 'text.primary', borderRadius: mine ? '20px 20px 6px 20px' : '20px 20px 20px 6px', px: 2, py: 1.1, mt: atts.some((a) => isImage(a.mimeType) || isVideo(a.mimeType)) ? 0.5 : 0, boxShadow: mine ? '0 6px 16px rgba(0,90,128,0.35)' : '0 4px 14px rgba(0,60,90,0.14)', border: mine ? 'none' : '1.5px solid', borderColor: 'rgba(0,136,181,0.45)' }}>
                                {hasText && (
                                  <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.5, fontWeight: 500, wordBreak: 'break-word' }}>{msg.text}</Typography>
                                )}
                                {atts.filter((a) => !isImage(a.mimeType) && !isVideo(a.mimeType)).map((att, i) => (
                                  <AttachmentItem
                                    key={i}
                                    att={att}
                                    mine={mine}
                                    onImageClick={setLightboxUrl}
                                    onPdfClick={setPdfViewUrl}
                                  />
                                ))}
                                <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 600, display: 'block', mt: 0.25 }}>
                                  {formatTime(msg.createdAt)}
                                </Typography>
                              </Box>
                            )}

                            {/* Timestamp when bubble is absent (media-only) */}
                            {!hasText && !atts.some((a) => !isImage(a.mimeType) && !isVideo(a.mimeType)) && atts.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                                {formatTime(msg.createdAt)}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      );
                    })}
                    <div ref={endRef} />
                  </>
                )}
              </Box>

              {/* Input area */}
              <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                {/* File previews strip */}
                {attachFiles.length > 0 && (
                  <Box sx={{ px: 2, pt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {attachFiles.map((f, i) => (
                      <AttachPreviewChip
                        key={i}
                        file={f}
                        previewUrl={attachPreviews[i]}
                        onRemove={() => removeAttach(i)}
                      />
                    ))}
                  </Box>
                )}

                <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleAttachSelect}
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar"
                  />

                  <Tooltip title={`Attach file (max ${chatFileSizeLimitMB != null ? chatFileSizeLimitMB : 50} MB each, up to 10)`}>
                    <IconButton
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sendingMessage || attachFiles.length >= 10}
                      sx={{ mb: 0.5, flexShrink: 0 }}
                    >
                      <AttachFileRounded />
                    </IconButton>
                  </Tooltip>

                  <TextField
                    fullWidth
                    placeholder="Type your message…"
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />

                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={!canSend}
                    startIcon={sendingMessage ? <CircularProgress size={18} color="inherit" /> : <SendRounded />}
                    sx={{ flexShrink: 0, mb: 0.25 }}
                  >
                    Send
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, py: 8 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>No chat selected</Typography>
              <Typography color="text.secondary" align="center" sx={{ maxWidth: 360 }}>
                Accept a request or pick a conversation from the left pane to start chatting.
              </Typography>
            </Stack>
          )}
        </Paper>
      </Stack>

      {/* ===== Image Lightbox ===== */}
      <Dialog open={!!lightboxUrl} onClose={() => setLightboxUrl(null)} maxWidth="lg">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setLightboxUrl(null)} size="small"><CloseRounded /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 1, bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt="Preview"
              style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 12 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', bgcolor: '#000', pb: 1.5 }}>
          {lightboxUrl && (
            <Button
              component="a"
              href={lightboxUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<DownloadRounded />}
              variant="outlined"
              size="small"
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ===== PDF Viewer ===== */}
      <Dialog open={!!pdfViewUrl} onClose={() => setPdfViewUrl(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PictureAsPdfRounded color="error" />
            <Typography fontWeight={700}>PDF Preview</Typography>
          </Stack>
          <IconButton onClick={() => setPdfViewUrl(null)} size="small"><CloseRounded /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '75vh' }}>
          {pdfViewUrl && (
            <iframe
              title="PDF preview"
              src={pdfViewUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {pdfViewUrl && (
            <Button
              component="a"
              href={pdfViewUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<DownloadRounded />}
            >
              Download
            </Button>
          )}
          <Button onClick={() => setPdfViewUrl(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default Chat;
